import React, { useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
import { getSalesHeaders as h } from '../../utils/salesAuth';

const EMAIL_ACTIONS = [
  { key: 'new_lead_response', label: 'New Lead Response', icon: '👋' },
  { key: 'estimate_delivery', label: 'Estimate Delivery', icon: '📋' },
  { key: 'estimate_followup', label: 'Estimate Follow-Up', icon: '📩' },
  { key: 'second_followup', label: 'Second Follow-Up', icon: '📨' },
  { key: 'final_followup', label: 'Final Follow-Up', icon: '🏁' },
  { key: 'site_visit_confirmation', label: 'Site Visit Confirmation', icon: '📍' },
  { key: 'site_visit_followup', label: 'Site Visit Follow-Up', icon: '📝' },
  { key: 'financing_option', label: 'Financing Option', icon: '💰' },
  { key: 'revised_estimate', label: 'Revised Estimate', icon: '🔄' },
  { key: 'deposit_request', label: 'Deposit Request', icon: '✅' },
  { key: 'customer_reengagement', label: 'Customer Re-Engagement', icon: '🔔' },
  { key: 'custom', label: 'Custom', icon: '✏️' },
];

const HEALTH_COLORS = { HOT: '#dc2626', WARM: '#f59e0b', COLD: '#3b82f6', AT_RISK: '#7c3aed' };
const HEALTH_BG = { HOT: '#fef2f2', WARM: '#fefce8', COLD: '#eff6ff', AT_RISK: '#f5f3ff' };

function FeedbackButtons({ auditId, onFeedback }) {
  const [sent, setSent] = useState(null);
  const send = async (fb) => {
    setSent(fb);
    await fetch(`${API}/api/sales/ai/feedback`, { method: 'PUT', credentials: 'include', headers: h(), body: JSON.stringify({ audit_id: auditId, feedback: fb }) });
    if (onFeedback) onFeedback(fb);
  };
  if (sent) return <span style={{ fontSize: 11, color: '#94a3b8' }}>{sent === 'helpful' ? 'Thanks!' : 'Noted'}</span>;
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      <button data-testid="ai-feedback-helpful" onClick={() => send('helpful')} style={{ padding: '2px 8px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 11, cursor: 'pointer', background: '#f8fafc' }}>👍 Helpful</button>
      <button data-testid="ai-feedback-not-helpful" onClick={() => send('not_helpful')} style={{ padding: '2px 8px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 11, cursor: 'pointer', background: '#f8fafc' }}>👎 Not Helpful</button>
    </div>
  );
}

// ── AI Email Writer ──
export function AIEmailWriter({ oppId, onDraftReady }) {
  const [action, setAction] = useState('');
  const [customInstr, setCustomInstr] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const generate = async () => {
    if (!action) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/sales/ai/draft-email`, {
        method: 'POST', credentials: 'include', headers: h(),
        body: JSON.stringify({ opportunity_id: oppId, action_type: action, custom_instructions: customInstr }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult(data);
        // Track as generated
        await fetch(`${API}/api/sales/ai/outcome`, { method: 'PUT', credentials: 'include', headers: h(), body: JSON.stringify({ audit_id: data.audit_id, outcome: 'generated' }) });
      } else {
        setError(data.detail || 'Failed to generate');
      }
    } catch { setError('Network error'); }
    setLoading(false);
  };

  const useInComposer = async () => {
    if (result && onDraftReady) {
      onDraftReady({ subject: result.subject, body: result.body });
      await fetch(`${API}/api/sales/ai/outcome`, { method: 'PUT', credentials: 'include', headers: h(), body: JSON.stringify({ audit_id: result.audit_id, outcome: 'accepted' }) });
    }
  };

  const discard = async () => {
    if (result?.audit_id) {
      await fetch(`${API}/api/sales/ai/outcome`, { method: 'PUT', credentials: 'include', headers: h(), body: JSON.stringify({ audit_id: result.audit_id, outcome: 'discarded' }) });
    }
    setResult(null);
    setAction('');
  };

  return (
    <div data-testid="ai-email-writer" style={{ background: '#fafbff', border: '1px solid #e0e7ff', borderRadius: 8, padding: 14, marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#4338ca', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 20, height: 20, background: '#4338ca', borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 800 }}>AI</span>
        Draft Email with AI
      </div>

      {!result ? (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
            {EMAIL_ACTIONS.map(a => (
              <button key={a.key} data-testid={`ai-email-${a.key}`} onClick={() => setAction(a.key)}
                style={{
                  padding: '7px 12px', border: action === a.key ? '2px solid #4338ca' : '1px solid #d1d5db',
                  borderRadius: 6, fontSize: 11, cursor: 'pointer',
                  background: action === a.key ? '#eef2ff' : '#fff', color: action === a.key ? '#4338ca' : '#475569',
                  fontWeight: action === a.key ? 600 : 400, minHeight: 36,
                }}>
                {a.icon} {a.label}
              </button>
            ))}
          </div>
          {action === 'custom' && (
            <textarea value={customInstr} onChange={e => setCustomInstr(e.target.value)} placeholder="Describe what you want the email to say..."
              rows={2} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }} />
          )}
          <button data-testid="ai-generate-email-btn" onClick={generate} disabled={!action || loading}
            style={{ padding: '8px 16px', background: action && !loading ? '#4338ca' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: action && !loading ? 'pointer' : 'default', minHeight: 40 }}>
            {loading ? 'Generating...' : 'Generate Draft'}
          </button>
          {error && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{error}</p>}
        </>
      ) : (
        <>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: 12, marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Subject</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{result.subject}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 10, marginBottom: 4 }}>Body</div>
            <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.6, maxHeight: 200, overflowY: 'auto' }}>{result.body}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button data-testid="ai-use-draft-btn" onClick={useInComposer} style={{ padding: '8px 16px', background: '#4338ca', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 40 }}>
              Use in Composer
            </button>
            <button onClick={discard} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#64748b', minHeight: 40 }}>
              Discard
            </button>
            <button onClick={() => { setResult(null); }} style={{ padding: '8px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#475569', minHeight: 40 }}>
              Regenerate
            </button>
            <FeedbackButtons auditId={result.audit_id} />
          </div>
        </>
      )}
    </div>
  );
}


// ── AI Sidebar Panel (Health + Summary + Next Steps + Call Prep + Notes Cleanup + Site Visit) ──
export function AISidebar({ oppId }) {
  const [activePanel, setActivePanel] = useState(null);
  const [loading, setLoading] = useState('');
  const [data, setData] = useState({});
  const [notesInput, setNotesInput] = useState('');
  const [extractInput, setExtractInput] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  const runAI = useCallback(async (action, endpoint, body) => {
    setLoading(action);
    setActivePanel(action);
    try {
      const res = await fetch(`${API}/api/sales/ai/${endpoint}`, {
        method: 'POST', credentials: 'include', headers: h(), body: JSON.stringify(body),
      });
      const result = await res.json();
      setData(prev => ({ ...prev, [action]: result }));
    } catch (e) {
      setData(prev => ({ ...prev, [action]: { error: 'Failed to load' } }));
    }
    setLoading('');
  }, []);

  const saveCleanedNotes = async () => {
    if (!data.notes?.cleaned_note) return;
    setSavingNotes(true);
    try {
      const res = await fetch(`${API}/api/sales/ai/save-cleaned-notes`, {
        method: 'POST', credentials: 'include', headers: h(),
        body: JSON.stringify({
          opportunity_id: oppId,
          cleaned_notes: data.notes.cleaned_note,
          reanalyze_expansion: true,
        }),
      });
      if (res.ok) {
        const result = await res.json();
        setNotesSaved(true);
        setData(prev => ({
          ...prev,
          notesSaveResult: result,
        }));
        setTimeout(() => setNotesSaved(false), 5000);
      }
    } finally { setSavingNotes(false); }
  };

  const AIBtn = ({ action, label, icon, endpoint, body }) => (
    <button data-testid={`ai-${action}-btn`}
      onClick={() => activePanel === action ? setActivePanel(null) : runAI(action, endpoint, body || { opportunity_id: oppId })}
      style={{
        width: '100%', padding: '10px 12px', border: activePanel === action ? '1px solid #818cf8' : '1px solid #e2e8f0',
        borderRadius: 6, fontSize: 12, cursor: 'pointer', textAlign: 'left',
        background: activePanel === action ? '#eef2ff' : '#fafbff',
        color: activePanel === action ? '#4338ca' : '#475569', fontWeight: 500,
        display: 'flex', alignItems: 'center', gap: 8, minHeight: 44,
      }}>
      <span style={{ fontSize: 14, width: 20, textAlign: 'center' }}>{icon}</span>
      {loading === action ? 'Loading...' : label}
    </button>
  );

  return (
    <div data-testid="ai-sidebar" style={{ marginTop: 20 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#4338ca', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 20, height: 20, background: '#4338ca', borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 800 }}>AI</span>
        Sales Copilot
      </h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <AIBtn action="health" label="Opportunity Health" icon="🩺" endpoint="opportunity-health" />
        <AIBtn action="summary" label="Summarize Customer" icon="📊" endpoint="summarize" />
        <AIBtn action="nextsteps" label="Suggest Next Steps" icon="🎯" endpoint="suggest-next-steps" />
        <AIBtn action="callprep" label="Prepare for Call" icon="📞" endpoint="call-prep" />
        <AIBtn action="sitevisit" label="Site Visit Checklist" icon="📋" endpoint="site-visit-checklist" />
      </div>

      {/* Notes Cleanup */}
      <div style={{ marginTop: 8 }}>
        <button data-testid="ai-clean-notes-btn"
          onClick={() => setActivePanel(activePanel === 'notes' ? null : 'notes')}
          style={{
            width: '100%', padding: '10px 12px', border: activePanel === 'notes' ? '1px solid #818cf8' : '1px solid #e2e8f0',
            borderRadius: 6, fontSize: 12, cursor: 'pointer', textAlign: 'left',
            background: activePanel === 'notes' ? '#eef2ff' : '#fafbff',
            color: activePanel === 'notes' ? '#4338ca' : '#475569', fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 8, minHeight: 44,
          }}>
          <span style={{ fontSize: 14, width: 20, textAlign: 'center' }}>🧹</span>Clean Up Notes
        </button>
      </div>

      {/* Data Extraction */}
      <div style={{ marginTop: 4 }}>
        <button data-testid="ai-extract-data-btn"
          onClick={() => setActivePanel(activePanel === 'extract' ? null : 'extract')}
          style={{
            width: '100%', padding: '10px 12px', border: activePanel === 'extract' ? '1px solid #818cf8' : '1px solid #e2e8f0',
            borderRadius: 6, fontSize: 12, cursor: 'pointer', textAlign: 'left',
            background: activePanel === 'extract' ? '#eef2ff' : '#fafbff',
            color: activePanel === 'extract' ? '#4338ca' : '#475569', fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 8, minHeight: 44,
          }}>
          <span style={{ fontSize: 14, width: 20, textAlign: 'center' }}>🔍</span>Extract Project Data
        </button>
      </div>

      {/* ── Result Panels ── */}
      {activePanel === 'health' && data.health && <HealthPanel data={data.health} />}
      {activePanel === 'summary' && data.summary && <SummaryPanel data={data.summary} />}
      {activePanel === 'nextsteps' && data.nextsteps && <NextStepsPanel data={data.nextsteps} oppId={oppId} />}
      {activePanel === 'callprep' && data.callprep && <CallPrepPanel data={data.callprep} />}
      {activePanel === 'sitevisit' && data.sitevisit && <SiteVisitChecklistPanel data={data.sitevisit} />}

      {activePanel === 'notes' && (
        <div style={{ marginTop: 8, background: '#fff', border: '1px solid #e0e7ff', borderRadius: 8, padding: 12 }}>
          <textarea data-testid="notes-input" value={notesInput} onChange={e => setNotesInput(e.target.value)}
            placeholder="Paste rough notes here..." rows={3}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }} />
          <button onClick={() => { setNotesSaved(false); runAI('notes', 'clean-notes', { opportunity_id: oppId, raw_notes: notesInput }); }}
            disabled={!notesInput.trim() || loading === 'notes'}
            style={{ padding: '6px 14px', background: notesInput.trim() ? '#4338ca' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: notesInput.trim() ? 'pointer' : 'default' }}>
            {loading === 'notes' ? 'Cleaning...' : 'Clean Up'}
          </button>
          {data.notes && !data.notes.error && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>Cleaned Note:</div>
              <div style={{ fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap', background: '#f8fafc', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}>
                {data.notes.cleaned_note}
              </div>
              {data.notes.extracted_action_items?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>Action Items:</div>
                  {data.notes.extracted_action_items.map((item, i) => (
                    <div key={i} style={{ fontSize: 11, color: '#475569', paddingLeft: 8 }}>• {item}</div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                <button data-testid="save-reanalyze-btn" onClick={saveCleanedNotes} disabled={savingNotes || notesSaved}
                  style={{ padding: '6px 14px', background: notesSaved ? '#16a34a' : savingNotes ? '#94a3b8' : '#c0392b', color: '#fff',
                    border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: savingNotes || notesSaved ? 'default' : 'pointer' }}>
                  {notesSaved ? 'Saved & Re-Analyzed' : savingNotes ? 'Saving...' : 'Save Notes & Re-Analyze'}
                </button>
                <FeedbackButtons auditId={data.notes.audit_id} />
              </div>
              {notesSaved && data.notesSaveResult?.expansion_reanalyzed && (
                <div style={{ marginTop: 6, fontSize: 11, color: '#16a34a', background: '#f0fdf4', padding: '6px 8px', borderRadius: 4, border: '1px solid #bbf7d0' }}>
                  Notes saved to opportunity. Expansion re-analyzed: <strong>{data.notesSaveResult.expansion_score}</strong>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activePanel === 'extract' && (
        <div style={{ marginTop: 8, background: '#fff', border: '1px solid #e0e7ff', borderRadius: 8, padding: 12 }}>
          <textarea data-testid="extract-input" value={extractInput} onChange={e => setExtractInput(e.target.value)}
            placeholder='Paste customer description, email, or notes. E.g. "We need about 740 feet of 5-foot top rail fence with two 12-foot gates painted black"'
            rows={3} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }} />
          <button onClick={() => runAI('extract', 'extract-data', { opportunity_id: oppId, text_input: extractInput })}
            disabled={!extractInput.trim() || loading === 'extract'}
            style={{ padding: '6px 14px', background: extractInput.trim() ? '#4338ca' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: extractInput.trim() ? 'pointer' : 'default' }}>
            {loading === 'extract' ? 'Extracting...' : 'Extract Data'}
          </button>
          {data.extract && !data.extract.error && <DataExtractionPanel data={data.extract} />}
        </div>
      )}
    </div>
  );
}


// ── Sub-panels ──

function HealthPanel({ data }) {
  const health = data.health || 'WARM';
  return (
    <div data-testid="ai-health-panel" style={{ marginTop: 8, background: HEALTH_BG[health] || '#f8fafc', border: `1px solid ${HEALTH_COLORS[health] || '#d1d5db'}40`, borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span data-testid="health-badge" style={{
          padding: '3px 12px', borderRadius: 4, fontSize: 12, fontWeight: 800, letterSpacing: 1,
          background: HEALTH_COLORS[health] || '#64748b', color: '#fff',
        }}>{health.replace('_', ' ')}</span>
        <span style={{ fontSize: 11, color: '#64748b' }}>Confidence: {data.confidence || 'medium'}</span>
      </div>
      <p style={{ fontSize: 12, color: '#374151', lineHeight: 1.5, margin: '0 0 8px' }}>{data.reasoning}</p>
      {data.positive_signals?.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#16a34a' }}>Positive Signals</div>
          {data.positive_signals.map((s, i) => <div key={i} style={{ fontSize: 11, color: '#475569', paddingLeft: 8 }}>+ {s}</div>)}
        </div>
      )}
      {data.risk_factors?.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#dc2626' }}>Risk Factors</div>
          {data.risk_factors.map((s, i) => <div key={i} style={{ fontSize: 11, color: '#475569', paddingLeft: 8 }}>- {s}</div>)}
        </div>
      )}
      <FeedbackButtons auditId={data.audit_id} />
    </div>
  );
}

function SummaryPanel({ data }) {
  const s = data.summary || {};
  const fields = [
    ['Customer', s.customer_name], ['Project', s.project], ['Value', s.estimated_value],
    ['Stage', s.stage], ['Last Contact', s.last_contact], ['Primary Concern', s.primary_concern],
    ['Financing', s.financing_status], ['Next Follow-Up', s.next_followup],
    ['Recommended Action', s.recommended_action],
  ];
  return (
    <div data-testid="ai-summary-panel" style={{ marginTop: 8, background: '#fff', border: '1px solid #e0e7ff', borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Customer Brief</div>
      {fields.map(([label, val]) => val ? (
        <div key={label} style={{ display: 'flex', gap: 4, padding: '3px 0', borderBottom: '1px solid #f1f5f9', fontSize: 11 }}>
          <span style={{ color: '#64748b', minWidth: 90, flexShrink: 0, fontWeight: 600 }}>{label}</span>
          <span style={{ color: '#1e293b', fontWeight: label === 'Recommended Action' ? 600 : 400 }}>{val}</span>
        </div>
      ) : null)}
      {s.key_notes && <p style={{ fontSize: 11, color: '#475569', marginTop: 6, fontStyle: 'italic' }}>{s.key_notes}</p>}
      <div style={{ marginTop: 8 }}><FeedbackButtons auditId={data.audit_id} /></div>
    </div>
  );
}

function NextStepsPanel({ data, oppId }) {
  const recs = data.recommendations || [];
  const [dismissed, setDismissed] = useState({});
  const [completed, setCompleted] = useState({});

  const markAction = async (idx, status) => {
    if (status === 'completed') setCompleted(prev => ({ ...prev, [idx]: true }));
    else setDismissed(prev => ({ ...prev, [idx]: true }));
  };

  const PRIORITY_COLORS = { high: '#dc2626', medium: '#f59e0b', low: '#64748b' };

  return (
    <div data-testid="ai-nextsteps-panel" style={{ marginTop: 8, background: '#fff', border: '1px solid #e0e7ff', borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Recommended Actions</div>
      {recs.map((r, i) => (
        <div key={i} style={{
          padding: '8px 10px', marginBottom: 6, borderRadius: 6, border: '1px solid #f1f5f9',
          background: completed[i] ? '#f0fdf4' : dismissed[i] ? '#f8fafc' : '#fff',
          opacity: dismissed[i] ? 0.5 : 1,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 700, background: `${PRIORITY_COLORS[r.priority]}15`, color: PRIORITY_COLORS[r.priority] }}>
              {r.priority?.toUpperCase()}
            </span>
            <span style={{ fontSize: 10, color: '#94a3b8' }}>{r.type}</span>
          </div>
          <p style={{ fontSize: 12, color: '#1e293b', fontWeight: 500, margin: '0 0 4px' }}>{r.action}</p>
          <p style={{ fontSize: 11, color: '#64748b', margin: 0 }}>{r.reasoning}</p>
          {!completed[i] && !dismissed[i] && (
            <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
              <button onClick={() => markAction(i, 'completed')} style={{ padding: '6px 12px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer', minHeight: 32 }}>Completed</button>
              <button onClick={() => markAction(i, 'dismissed')} style={{ padding: '6px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 10, cursor: 'pointer', color: '#64748b', minHeight: 32 }}>Dismiss</button>
            </div>
          )}
        </div>
      ))}
      <FeedbackButtons auditId={data.audit_id} />
    </div>
  );
}

function CallPrepPanel({ data }) {
  const b = data.briefing || {};
  const fields = [
    ['Customer', b.customer], ['Project', b.project], ['Estimate Status', b.estimate_status],
    ['Last Conversation', b.last_conversation], ['Customer Concerns', b.customer_concerns],
    ['Outstanding Questions', b.outstanding_questions], ['Financing Status', b.financing_status],
    ['Desired Outcome', b.desired_outcome],
  ];
  return (
    <div data-testid="ai-callprep-panel" style={{ marginTop: 8, background: '#fff', border: '1px solid #e0e7ff', borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Call Prep Briefing</div>
      {fields.map(([label, val]) => val ? (
        <div key={label} style={{ padding: '4px 0', borderBottom: '1px solid #f1f5f9', fontSize: 11 }}>
          <span style={{ color: '#64748b', fontWeight: 600 }}>{label}: </span>
          <span style={{ color: '#1e293b' }}>{val}</span>
        </div>
      ) : null)}
      {b.talking_points?.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#4338ca', marginBottom: 4 }}>Talking Points</div>
          {b.talking_points.map((p, i) => (
            <div key={i} style={{ fontSize: 11, color: '#475569', paddingLeft: 10, marginBottom: 3 }}>
              {i + 1}. {p}
            </div>
          ))}
        </div>
      )}
      {b.cross_sell_talking_points?.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#c0392b', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            Cross-Sell Talking Points
          </div>
          {b.cross_sell_talking_points.map((p, i) => (
            <div key={i} style={{ fontSize: 11, color: '#92400e', paddingLeft: 10, marginBottom: 4, background: '#fffbeb', padding: '4px 8px', borderRadius: 4, borderLeft: '2px solid #f59e0b' }}>
              {p}
            </div>
          ))}
        </div>
      )}
      {b.discovery_questions?.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginBottom: 4 }}>Discovery Questions</div>
          {b.discovery_questions.map((q, i) => (
            <div key={i} style={{ fontSize: 11, color: '#166534', paddingLeft: 10, marginBottom: 3, fontStyle: 'italic' }}>
              "{q}"
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 8 }}><FeedbackButtons auditId={data.audit_id} /></div>
    </div>
  );
}

function SiteVisitChecklistPanel({ data }) {
  const [checked, setChecked] = useState({});
  const checklist = data.checklist || [];
  const totalItems = checklist.reduce((sum, s) => sum + (s.items?.length || 0), 0);
  const checkedCount = Object.values(checked).filter(Boolean).length;

  const toggleItem = (sectionIdx, itemIdx) => {
    const key = `${sectionIdx}-${itemIdx}`;
    setChecked(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const TYPE_STYLES = {
    required: { bg: '#fef2f2', border: '#fecaca', dot: '#dc2626' },
    recommended: { bg: '#eff6ff', border: '#bfdbfe', dot: '#2563eb' },
    expansion: { bg: '#fffbeb', border: '#fde68a', dot: '#f59e0b' },
  };

  return (
    <div data-testid="ai-sitevisit-panel" style={{ marginTop: 8, background: '#fff', border: '1px solid #e0e7ff', borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>Site Visit Checklist</div>
        <div style={{ fontSize: 10, color: '#64748b' }}>{checkedCount}/{totalItems} completed</div>
      </div>
      {/* Progress bar */}
      <div style={{ height: 4, background: '#e2e8f0', borderRadius: 2, marginBottom: 10, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${totalItems ? (checkedCount / totalItems * 100) : 0}%`, background: '#16a34a', borderRadius: 2, transition: 'width .3s' }} />
      </div>
      {data.estimated_visit_duration && (
        <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 8 }}>Estimated duration: {data.estimated_visit_duration} min</div>
      )}
      {checklist.map((section, sIdx) => (
        <div key={sIdx} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            {section.section}
          </div>
          {(section.items || []).map((item, iIdx) => {
            const key = `${sIdx}-${iIdx}`;
            const isChecked = checked[key];
            const ts = TYPE_STYLES[item.type] || TYPE_STYLES.recommended;
            return (
              <label key={iIdx} data-testid={`checklist-item-${sIdx}-${iIdx}`}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '4px 6px', marginBottom: 2,
                  background: isChecked ? '#f0fdf4' : ts.bg, borderRadius: 4, cursor: 'pointer', border: `1px solid ${isChecked ? '#bbf7d0' : ts.border}`,
                  opacity: isChecked ? 0.7 : 1, transition: 'opacity .15s' }}>
                <input type="checkbox" checked={isChecked || false} onChange={() => toggleItem(sIdx, iIdx)}
                  style={{ marginTop: 2, accentColor: '#16a34a' }} />
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 11, color: isChecked ? '#16a34a' : '#1e293b', textDecoration: isChecked ? 'line-through' : 'none' }}>
                    {item.item}
                  </span>
                  {item.type === 'expansion' && (
                    <span style={{ fontSize: 8, marginLeft: 4, padding: '1px 4px', borderRadius: 2, background: '#fde68a', color: '#92400e', fontWeight: 700 }}>EXPANSION</span>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      ))}
      {data.notes_for_salesperson && (
        <div style={{ marginTop: 6, padding: '6px 8px', background: '#eff6ff', borderRadius: 4, border: '1px solid #bfdbfe', fontSize: 11, color: '#1e40af' }}>
          <strong>Note:</strong> {data.notes_for_salesperson}
        </div>
      )}
      <div style={{ marginTop: 8 }}><FeedbackButtons auditId={data.audit_id} /></div>
    </div>
  );
}

function DataExtractionPanel({ data }) {
  const ext = data.extracted || {};
  return (
    <div data-testid="ai-extract-panel" style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>Extracted Information</div>
      {ext.services?.length > 0 && (
        <div style={{ marginBottom: 6, fontSize: 11 }}>
          <span style={{ color: '#64748b', fontWeight: 600 }}>Services: </span>
          <span style={{ color: '#4338ca', fontWeight: 600 }}>{ext.services.join(', ')}</span>
        </div>
      )}
      {ext.details && Object.keys(ext.details).length > 0 && (
        <div style={{ background: '#f8fafc', padding: 8, borderRadius: 6, marginBottom: 6, fontSize: 11 }}>
          {Object.entries(ext.details).map(([k, v]) => (
            <div key={k} style={{ padding: '2px 0' }}>
              <span style={{ color: '#64748b' }}>{k}: </span>
              <span style={{ color: '#1e293b', fontWeight: 500 }}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
            </div>
          ))}
        </div>
      )}
      {data.missing_info?.length > 0 && (
        <div style={{ background: '#fefce8', border: '1px solid #fde68a', padding: 8, borderRadius: 6, marginBottom: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>Additional Information Required:</div>
          {data.missing_info.map((m, i) => <div key={i} style={{ fontSize: 11, color: '#78350f', paddingLeft: 8 }}>• {m}</div>)}
        </div>
      )}
      {data.raw_interpretation && (
        <p style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>{data.raw_interpretation}</p>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>Confidence: {data.confidence}</span>
        <FeedbackButtons auditId={data.audit_id} />
      </div>
    </div>
  );
}
